// Map registration fee (USD) -> GiveButter inline-form embed URL.
// These URLs come from each widget's `embed_url` field in the GiveButter
// elements config API. Using the iframe URL forces the inline donation form
// to render at any width — the <givebutter-widget> custom element only
// renders the inline form when the parent is at least 1024px wide, otherwise
// it falls back to a "Donate" button modal which is not what we want here.
const GIVEBUTTER_EMBEDS_BY_FEE = {
  1000: 'https://givebutter.com/embed/c/the-kenya-challenge-copy-mknqbe',
  500: 'https://givebutter.com/embed/c/the-kenya-challenge-copy-kxktha',
  250: 'https://givebutter.com/embed/c/the-kenya-challenge-250',
  95: 'https://givebutter.com/embed/c/the-kenya-challenge-95',
  25: 'https://givebutter.com/embed/c/the-kenya-challenge-25',
  5: 'https://givebutter.com/embed/c/the-kenya-challenge-5',
};

// M-Pesa embed URL (KEF-hosted page that handles 500/1000 KES payments).
const MPESA_EMBED_URL = 'https://mpesa.kenyaeducationfund.org/embed/kef-walk';

/**
 * Render M-Pesa (mobile money) and the matching GiveButter inline form
 * (credit card) for the chosen registration fee. Stacks vertically by
 * default and goes side-by-side on very wide desktops (xl breakpoint).
 */
export function PaymentWidgets({ feeUsd, title = 'Choose Payment Method' }) {
  const gbUrl = GIVEBUTTER_EMBEDS_BY_FEE[Number(feeUsd)] || null;

  return (
    <div data-testid="payment-widgets">
      <h2 className="text-2xl md:text-3xl font-bold text-stone-900 text-center mb-6">{title}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* M-Pesa */}
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-5 pt-6 pb-4 text-center">
            <div className="inline-flex w-12 h-12 rounded-xl bg-orange-50 items-center justify-center mb-3">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"/></svg>
            </div>
            <h3 className="text-lg font-bold text-stone-900">Pay with Mpesa</h3>
            <p className="text-sm text-stone-500 mt-1">Complete payment using mobile money</p>
          </div>
          <iframe
            src={MPESA_EMBED_URL}
            title="M-Pesa Payment"
            width="100%"
            height="900"
            frameBorder="0"
            className="block w-full border-t border-stone-100"
            data-testid="payment-mpesa-iframe"
          />
        </div>

        {/* GiveButter inline form (iframe embed) */}
        <div className="rounded-2xl border border-stone-200 bg-white overflow-hidden">
          <div className="px-5 pt-6 pb-4 text-center">
            <div className="inline-flex w-12 h-12 rounded-xl bg-stone-100 items-center justify-center mb-3">
              <svg className="w-6 h-6 text-stone-800" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M5 6h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2z"/></svg>
            </div>
            <h3 className="text-lg font-bold text-stone-900">Pay with Credit Card</h3>
            <p className="text-sm text-stone-500 mt-1">Secure payment via GiveButter</p>
          </div>
          {gbUrl ? (
            <iframe
              key={gbUrl}
              src={gbUrl}
              title="GiveButter Payment"
              width="100%"
              height="900"
              frameBorder="0"
              className="block w-full border-t border-stone-100"
              allow="payment *"
              data-testid="payment-givebutter-iframe"
            />
          ) : (
            <div className="p-4">
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800">
                No GiveButter widget configured for ${feeUsd}. Ask an admin to add the widget ID.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
